import { Show, SimpleShowLayout, TextField, useTranslate, FunctionField } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components';
import { Resources } from '../resource';
import { DashboardContent } from 'src/layouts/dashboard';
import { Box, Card, Chip, Stack, Typography, Divider, Link, Dialog, DialogContent, IconButton } from '@mui/material';
import { EmptyContent } from 'src/components/empty-content';
import XDateField from '../../components/XDateField';
import { Iconify } from 'src/components/iconify';
import { useState } from 'react';

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const getFileType = (url: string): 'image' | 'video' | 'other' => {
  if (!url) return 'other';
  const extension = url.toLowerCase().split('?')[0].split('.').pop();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) return 'image';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension || '')) return 'video';
  return 'other';
};

const MediaViewerDialog = ({ open, file, onClose }: { open: boolean; file: any; onClose: () => void }) => {
  if (!file) return null;
  const type = getFileType(file.url);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0, backgroundColor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 10 }}
        >
          <Iconify icon="eva:close-fill" width={20} />
        </IconButton>

        {type === 'image' && (
          <Box component="img" src={file.url} alt="preview" sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
        )}

        {type === 'video' && (
          <Box component="video" src={file.url} controls autoPlay sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
        )}

        {type !== 'image' && type !== 'video' && (
          <Box sx={{ color: '#fff', p: 4 }}>
            <Typography color="white">No preview available</Typography>
            <Link href={file.url} target="_blank" rel="noopener noreferrer" sx={{ color: '#fff', display: 'block', mt: 1 }}>
              Download
            </Link>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const UserFeedbackShow = () => {
  const translate = useTranslate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any | null>(null);

  const handleOpenDialog = (file: any) => {
    setSelectedFile(file);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
  };

  return (
    <PermissionGuard resource="user-feedback" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.userFeedback.title')}
          links={[
            { name: translate('module.userFeedback.title'), href: `/${Resources.userFeedback}` },
            { name: translate('module.userFeedback.detail') },
          ]}
        />

        <Show actions={false}>
          <Card>
            <SimpleShowLayout>
              {/* Basic Info */}
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  {translate('module.userFeedback.basicInfo')}
                </Typography>
                <TextField source="id" label="ID" />
                <TextField source="email" label="module.userFeedback.email" />
                <TextField source="userAddress" label="module.userFeedback.address" />
                <FunctionField
                  label="module.userFeedback.createdAt"
                  render={(record: any) => <XDateField value={record.createdAt} />}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Feedback Details */}
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  {translate('module.userFeedback.details')}
                </Typography>
                <TextField source="category" label="module.userFeedback.category" />

                <FunctionField
                  label="module.userFeedback.status"
                  render={(record: any) => (
                    <Chip
                      label={record.status}
                      color={getStatusColor(record.status)}
                      size="small"
                    />
                  )}
                />

                <FunctionField
                  label="module.userFeedback.feedback"
                  render={(record: any) => (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        minHeight: 100,
                      }}
                    >
                      <Typography variant="body2">{record.feedback}</Typography>
                    </Box>
                  )}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Attachments - Display Media Files */}
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  {translate('module.userFeedback.attachments')}
                </Typography>

                <FunctionField
                  render={(record: any) => (
                    <Box>
                      {record.cdnFile && record.cdnFile.length > 0 ? (
                        <Stack spacing={2} direction="row">
                          {record.cdnFile.map((file: any, index: number) => {
                            const fileType = getFileType(file.url);
                            return (
                              <Box
                                key={file.id}
                                onClick={() => handleOpenDialog(file)}
                                sx={{
                                  p: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  backgroundColor: 'background.default',
                                  cursor: 'pointer',
                                }}
                              >
                                {/* Image Preview */}
                                {fileType === 'image' && (
                                  <Box
                                    component="img"
                                    src={file.url}
                                    alt={`Attachment ${index + 1}`}
                                    sx={{
                                      maxWidth: '100%',
                                      height: 'auto',
                                      maxHeight: 300,
                                      borderRadius: 1,
                                      mb: 1,
                                    }}
                                  />
                                )}

                                {/* Video Preview */}
                                {fileType === 'video' && (
                                  <Box
                                    component="video"
                                    src={file.url}
                                    controls
                                    sx={{
                                      maxWidth: '100%',
                                      height: 'auto',
                                      maxHeight: 300,
                                      borderRadius: 1,
                                      mb: 1,
                                      backgroundColor: '#000',
                                    }}
                                  />
                                )}

                                {/* File Info */}
                                <Stack spacing={1}>

                                  <Link
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      mt: 1,
                                    }}
                                  >
                                    <Iconify icon="eva:download-fill" width={16} />
                                    Download
                                  </Link>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {translate('module.userFeedback.noAttachments')}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Replies */}
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                  {translate('module.userFeedback.replies')}
                </Typography>

                <FunctionField
                  render={(record: any) => (
                    <Box>
                      {record.reply && record.reply.length > 0 ? (
                        <Stack spacing={2}>
                          {record.reply.map((replyItem: any, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                p: 2,
                                bgcolor: 'background.default',
                                borderRadius: 1,
                                borderLeft: '4px solid primary.main',
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={600}>
                                {replyItem.author}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {replyItem.message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                <XDateField value={replyItem.createdAt} />
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {translate('module.userFeedback.noReplies')}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Stack>
            </SimpleShowLayout>
          </Card>
          <MediaViewerDialog open={openDialog} file={selectedFile} onClose={handleCloseDialog} />
        </Show>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default UserFeedbackShow;
